import React, { useState, useMemo } from 'react';
import { Form, Button, Alert, Badge } from 'react-bootstrap';

// Renders the lead-capture card that appears after the agent calls
// request_lead_handoff. The agent pre-fills the ZIP from space_context when it
// knows it; the user confirms or enters their ZIP and submits. We deliberately
// collect ONLY the ZIP — no phone, no email — so the homeowner never hands
// their contact details to a pile of contractors. Plum sources and evaluates
// vetted contractors on their behalf, which is how we keep the process
// spam-free. submittedValues / submittedState are passed in by the parent so
// the card persists across re-renders once the agent run completes.
//
// Props:
// - prefill: { zip, project_type, budget_range, timeline }
// - onSubmit: (values) => Promise<void>
// - onSkip: () => Promise<void>
// - disabled: boolean — true while parent is awaiting a callable
// - submittedState: 'captured' | 'skipped' | 'error' | null
// - errorMessage: string | null

function LeadHandoffCard({ prefill = {}, onSubmit, onSkip, disabled, submittedState, errorMessage }) {
  const initialZip = (prefill.zip || '').slice(0, 5);

  const [zip, setZip] = useState(initialZip);

  const zipValid = /^\d{5}$/.test(zip);

  const canSubmit = zipValid && !disabled;

  const chips = useMemo(() => {
    const out = [];
    if (prefill.project_type) out.push({ label: 'Project', value: prefill.project_type.replace(/_/g, ' ') });
    if (prefill.budget_range) out.push({ label: 'Budget', value: prefill.budget_range });
    if (prefill.timeline) out.push({ label: 'Timeline', value: prefill.timeline });
    return out;
  }, [prefill.project_type, prefill.budget_range, prefill.timeline]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      zip,
      projectType: prefill.project_type || '',
      budgetRange: prefill.budget_range || '',
      timeline: prefill.timeline || '',
    });
  };

  if (submittedState === 'captured') {
    return (
      <div className="lead-handoff-card lead-handoff-submitted">
        <div className="lead-handoff-header">
          <i className="bi bi-check2-circle text-success" />
          <strong>Request submitted</strong>
        </div>
        <p className="mb-0 text-muted small">
          We're sourcing and evaluating vetted contractors in your area. We'll share your matches with you here — no spam calls, ever.
        </p>
      </div>
    );
  }

  if (submittedState === 'skipped') {
    return (
      <div className="lead-handoff-card lead-handoff-submitted">
        <div className="lead-handoff-header">
          <i className="bi bi-x-circle text-muted" />
          <strong>Contractor sourcing skipped</strong>
        </div>
        <p className="mb-0 text-muted small">
          You can revisit this later from the project document.
        </p>
      </div>
    );
  }

  return (
    <Form className="lead-handoff-card" onSubmit={handleSubmit} noValidate>
      <div className="lead-handoff-header">
        <i className="bi bi-people-fill" />
        <strong>Find vetted local contractors</strong>
      </div>
      <p className="text-muted small mb-3">
        Share your ZIP and we'll source and evaluate contractors in your area for you. No phone number, no email — so no spam calls. We do the vetting; you compare the best quotes.
      </p>

      {chips.length > 0 && (
        <div className="lead-handoff-chips mb-3">
          {chips.map(chip => (
            <Badge key={chip.label} bg="light" text="dark" className="me-2 mb-1">
              <span className="text-muted">{chip.label}:</span> {chip.value}
            </Badge>
          ))}
        </div>
      )}

      <Form.Group className="mb-3" controlId="lead-zip">
        <Form.Label className="small mb-1">ZIP code</Form.Label>
        <Form.Control
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
          autoComplete="postal-code"
          inputMode="numeric"
          placeholder="12345"
          isInvalid={zip.length > 0 && !zipValid}
          disabled={disabled}
          required
        />
      </Form.Group>

      {errorMessage && (
        <Alert variant="danger" className="py-2 small mb-3">
          {errorMessage}
        </Alert>
      )}

      <div className="d-flex gap-2">
        <Button type="submit" variant="primary" disabled={!canSubmit}>
          {disabled ? 'Submitting…' : 'Find contractors'}
        </Button>
        <Button type="button" variant="outline-secondary" onClick={onSkip} disabled={disabled}>
          Not now
        </Button>
      </div>
    </Form>
  );
}

export default LeadHandoffCard;
